import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@common/hooks';
import { Button, Row } from 'antd';
import { Modal } from '@common/components';
import {
  CreateBackgroundIdentificationItem,
  UpdateBackgroundIdentificationItem,
  CreateImmigrationItem,
  UpdateImmigrationItem,
  CreateLicenseItem,
  UpdateLicenseItem,
  CreateCertificationItem,
  UpdateCertificationItem,
  CreateAssessmentItem,
  UpdateAssessmentItem,
  CreateHealthDocumentItem,
  UpdateHealthDocumentItem,
  CreateOtherDocumentItem,
  UpdateOtherDocumentItem,
} from 'works/graphql/works-list-item';

import {
  BACKGROUND_IDENTIFICATION_TYPE,
  IMMIGRATION_TYPE,
  LICENSE_TYPE,
  CERTIFICATION_TYPE,
  ASESSMENT_TYPE,
  HEALTH_DOC,
  OTHER_DOCUMENT,
} from './compliance-item-types';

export const OnboardingListEditActions = ({
  itemsAdded,
  itemsChanged,
  worksListId,
  onCancel,
  onSave,
}) => {
  const [pending, setPending] = useState(false);

  const [createBackgroundIdentificationItem] = useMutation(
    CreateBackgroundIdentificationItem
  );
  const [createImmigrationItem] = useMutation(CreateImmigrationItem);
  const [createLicenseItem] = useMutation(CreateLicenseItem);
  const [createCertificationItem] = useMutation(CreateCertificationItem);
  const [createAssessmentItem] = useMutation(CreateAssessmentItem);
  const [createHealthDocumentItem] = useMutation(CreateHealthDocumentItem);
  const [createOtherDocumentItem] = useMutation(CreateOtherDocumentItem);

  const [updateBackgroundIdentificationItem] = useMutation(
    UpdateBackgroundIdentificationItem,
    {
      refetchQueries: ['CandidateAssignmentQuery'],
    }
  );
  const [updateImmigrationItem] = useMutation(UpdateImmigrationItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });
  const [updateLicenseItem] = useMutation(UpdateLicenseItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });
  const [updateCertificationItem] = useMutation(UpdateCertificationItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });
  const [updateAssessmentItem] = useMutation(UpdateAssessmentItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });
  const [updateHealthDocumentItem] = useMutation(UpdateHealthDocumentItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });
  const [updateOtherDocumentItem] = useMutation(UpdateOtherDocumentItem, {
    refetchQueries: ['CandidateAssignmentQuery'],
  });

  const ItemTypeCreateMutations = {
    [BACKGROUND_IDENTIFICATION_TYPE]: createBackgroundIdentificationItem,
    [IMMIGRATION_TYPE]: createImmigrationItem,
    [LICENSE_TYPE]: createLicenseItem,
    [CERTIFICATION_TYPE]: createCertificationItem,
    [ASESSMENT_TYPE]: createAssessmentItem,
    [HEALTH_DOC]: createHealthDocumentItem,
    [OTHER_DOCUMENT]: createOtherDocumentItem,
  };

  const ItemTypeUpdateMutations = {
    [BACKGROUND_IDENTIFICATION_TYPE]: updateBackgroundIdentificationItem,
    [IMMIGRATION_TYPE]: updateImmigrationItem,
    [LICENSE_TYPE]: updateLicenseItem,
    [CERTIFICATION_TYPE]: updateCertificationItem,
    [ASESSMENT_TYPE]: updateAssessmentItem,
    [HEALTH_DOC]: updateHealthDocumentItem,
    [OTHER_DOCUMENT]: updateOtherDocumentItem,
  };

  const handleSave = () => {
    if (!pending) {
      setPending(true);
      Promise.all([
        ...itemsChanged.map((item) => {
          const { type, id, ...input } = item;

          return ItemTypeUpdateMutations[type]({
            variables: {
              itemId: id,
              input,
            },
          });
        }),
        ...itemsAdded.map((item) => {
          const { type, id: _, ...input } = item;

          return ItemTypeCreateMutations[type]({
            variables: {
              worksListId,
              input,
            },
          });
        }),
      ]).then(() => {
        setPending(false);
        onSave();
      });
    }
  };

  const openConfirmationModal = () => {
    Modal.confirm({
      title: "You're about to save and update this compliance list",
      content:
        'This compliance list will not get any future updates from the compliance template once any changes are saved.',
      okText: 'Save',
      onOk: handleSave,
      className: 'compliance-list-update',
    });
  };

  return (
    <Row
      justify="end"
      style={{ gap: '8px', padding: '8px 16px', marginTop: '8px' }}
    >
      <Button onClick={onCancel}>Cancel</Button>
      <Button
        type="primary"
        disabled={itemsAdded.length === 0 && itemsChanged.length === 0}
        onClick={openConfirmationModal}
      >
        Save Changes
      </Button>
      <style jsx>{`
        :global(.compliance-list-update .ant-modal-content) {
          width: 500px;
        }
        :global(.compliance-list-update .ant-modal-confirm-body) {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        :global(
          .compliance-list-update
            .ant-modal-confirm-body
            .ant-modal-confirm-title
        ) {
          font-weight: bold;
        }
      `}</style>
    </Row>
  );
};

OnboardingListEditActions.propTypes = {
  itemsChanged: PropTypes.array.isRequired,
  itemsAdded: PropTypes.array.isRequired,
  worksListId: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
